import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CaretRight } from "@phosphor-icons/react";
import { videoQueryOptions } from "@/api-queries/videos";
import { VideoPlayer } from "@/components/video/VideoPlayer";
import { VideoCard } from "@/components/video/VideoCard";
import { ShareButton } from "@/components/blog/ShareButton";
import { formatDate } from "@/lib/format";
import { formatVideoDuration } from "@/lib/video-embed";

export const Route = createFileRoute("/video/$slug")({
  component: VideoDetailPage,
});

function VideoDetailPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { data: video, isLoading, error } = useQuery(videoQueryOptions(slug));

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-muted" />
          <div className="aspect-video rounded-2xl bg-muted" />
          <div className="h-9 w-3/4 rounded bg-muted" />
          <div className="h-4 w-full rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("videoPost.notFound")}
          </h1>
          <p className="mt-2 text-muted-foreground">{t("videoPost.notFoundDesc")}</p>
          <Link to="/video" className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
            {t("videoPost.backToVideos")}
          </Link>
        </div>
      </div>
    );
  }

  const duration = formatVideoDuration(video.durationSec);

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <nav className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">{t("videoPost.breadcrumbHome")}</Link>
          <CaretRight className="size-3.5" />
          <Link to="/video" className="hover:text-foreground">{t("videoPost.breadcrumbVideos")}</Link>
        </nav>

        <VideoPlayer video={video} />

        <header className="mt-8">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <time dateTime={video.publishedAt ?? video.createdAt}>{formatDate(video.publishedAt ?? video.createdAt)}</time>
            {duration && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{duration}</span>
              </>
            )}
            <span className="ml-auto">
              <ShareButton url={`${typeof window !== "undefined" ? window.location.origin : ""}/video/${video.slug}`} title={video.title} />
            </span>
          </div>
          <h1
            className="mt-3 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {video.title}
          </h1>
        </header>

        {video.description && (
          <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground/85">
            {video.description.split(/\n{2,}/).map((p, i) => (
              <p key={i} className="whitespace-pre-line">{p}</p>
            ))}
          </div>
        )}

        {video.tags.length > 0 && (
          <ul className="mt-8 flex flex-wrap gap-2" aria-label={t("videoPost.tagsLabel")}>
            {video.tags.map((tag) => (
              <li key={tag.id}>
                <Link
                  to="/video"
                  search={{ tag: tag.slug }}
                  className="rounded-full border border-border bg-white px-3.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {tag.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {video.related.length > 0 && (
        <section className="border-t border-border bg-muted/20 py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
              {t("videoPost.relatedTitle")}
            </h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {video.related.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
